import {
	ConnectionManager,
	ModelData,
	ProductData,
	SceneObject,
	TransformPosition,
	TransformVector,
	View3dElement,
	GridTools,
	Model,
	Product,
} from '@ikea-aoa/component-view-3d';
import { MeshRenderer, LineRenderer, Text, TransformRotation, Node } from '@ikea-aoa/elements-3d';
import { CoverItem, Platform, RangeSettings } from 'sofa-reborn-react';
import { ProductUtils } from '../../util/ProductUtils';
import { ModuleConnectionManager } from '../../util/SceneUtils/ModuleConnectionManager';
import { ConnectionKitsManager } from '../../util/SceneUtils/ConnectionKitsManager';
import { AssetsUrls } from '../../util/SceneUtils/AssetsUrls';
import { LegsManager } from './ObjectManager/LegsManager';
import { AutomaticallyAddManager } from './ObjectManager/AutomaticallyAddManager';
import { CushionManager } from './ObjectManager/CushionsManager';
type Props = {
	rangeSettings: RangeSettings;
	products: ProductData;
	sofaModels: ModelData;
	assetFolder: string | null;
	loadingModels: boolean;
	cover: CoverItem;
	selectedArmrest: Product | null;
	platform: Platform;
	showFunctionSleep?: boolean | undefined;
	showFunctionStorage?: boolean | undefined;
	showFunctions?: boolean | undefined;
	showDebug?: boolean | undefined;
	specificItems: Map<string, SceneObject>;
};

export class ObjectManager {
	private id = 0;
	private colorId;

	public sceneItems: Map<string, SceneObject>;
	public connectionManager: ConnectionManager;

	public automaticallyAddManager: AutomaticallyAddManager;
	public legsManager: LegsManager;
	public cushionManager: CushionManager;
	constructor(
		private view3d: View3dElement,
		public moduleManager: ModuleConnectionManager,
		public connectionKitsManager: ConnectionKitsManager,
		public gridTools: GridTools,
		private props: Props
	) {
		this.sceneItems = this.moduleManager.sceneItems;
		this.connectionManager = this.moduleManager.connectionManager;

		this.automaticallyAddManager = new AutomaticallyAddManager(this, this.props);
		this.legsManager = new LegsManager(this, this.props);
		this.cushionManager = new CushionManager(this, this.props);
	}

	/**
	 * Update props.
	 *
	 * @param props Props.
	 */
	public updateProps(props: Props): void {
		this.props = props;
		this.updateColorId();
		this.automaticallyAddManager.updateProps(props);
		this.legsManager.updateProps(props);
		this.cushionManager.updateProps(props);
	}

	/**
	 * Set color id.
	 *
	 * @param colorId Color name.
	 */
	public setColorId(colorId: string | null): void {
		this.colorId = colorId;
	}

	/**
	 * Creates a new object and adds it to sceneItems map.
	 *
	 * @param articleId Article id.
	 * @param position Object position.
	 * @param addedWithClick Boolean if object was added with click.
	 * @param colorId Object color id.
	 * @param id Optional object id.
	 *
	 */
	public createObject(
		articleId: string,
		position: TransformPosition = TransformVector.zero(),
		addedWithClick = false,
		colorId: string = this.colorId,
		id = this.getId()
	): SceneObject | null {
		const { x, y, z } = position;
		const model = this.connectionManager.getModelData(articleId);
		const color = colorId || this.colorId;
		if (!model || !color) {
			return null;
		}
		const size = model.bounds['default'].size;
		const pos = { x, y: y ? y : size.height / 2, z };

		const assetUrl = AssetsUrls.createUrlForAssetId(
			this.props.assetFolder,
			this.props.rangeSettings.assets3dSrc,
			model.modelId,
			this.props.rangeSettings.modelSwitchMap || null,
			color,
			this.showFeatures(model)
		);
		const isPreloaded = this.view3d.isPreloaded(assetUrl);

		try {
			const node = this.view3d.createObject(id, pos, assetUrl, 'MeshRenderer');

			if (!isPreloaded) {
				if (this.props.platform === 'kiosk' && !this.props.loadingModels) {
					setTimeout(function () {
						alert('Models can not be loaded, please contact kiosk support!');
					}, 1);
				} else {
					const meshRenderer = node.addComponent(MeshRenderer, 'MeshRenderer');
					meshRenderer.state.mesh = size;
				}
			}

			if (model.bounds.extended) {
				const { position, size } = model.bounds.extended;
				const line = node.addComponent(LineRenderer, 'BoundsRenderer');
				const y = -size.height / 2;

				// To not stick out under back of sofa
				const BACK_OFFSET = 1;

				line.state.points = [
					{ x: size.width / 2, y, z: -size.depth / 2 + BACK_OFFSET },
					{ x: size.width / 2, y, z: size.depth / 2 },
					{ x: -size.width / 2, y, z: size.depth / 2 },
					{ x: -size.width / 2, y, z: -size.depth / 2 + BACK_OFFSET },
				];

				if (line.state.transform) {
					line.state.transform.position = position;
					line.state.color = { r: 0.5, g: 0.5, b: 0.5 };
					line.state.isDashed = true;
					line.state.dashSize = 6;
					line.state.gapSize = 4;
					line.state.dashNb = 144;
				}
			}

			// DEBUG
			if (this.props.showDebug) {
				const line = node.addComponent(LineRenderer, 'LineRenderer');
				const y = size.height / 2;
				line.state.points = [
					{ x: size.width / 2, y, z: size.depth / 2 },
					{ x: size.width / 2, y, z: -size.depth / 2 },
					{ x: -size.width / 2, y, z: -size.depth / 2 },
					{ x: -size.width / 2, y, z: size.depth / 2 },
					{ x: size.width / 2, y, z: size.depth / 2 }, // back point
				];
				line.state.color = { r: 1, g: 1, b: 0 };
				line.state.isDashed = true;
				line.state.dashSize = 5;
				line.state.gapSize = 3;
				line.state.dashNb = 50;

				const text = node.addComponent(Text, 'Text');
				// text.state.text = model.modelId + ' - ' + id;
				text.state.text = id;
				text.state.color = 'white';
				text.state.fontSize = 16;
				text.state.borderColor = 'black';
				text.state.borderRadius = 5;
				text.state.backgroundColor = 'grey';
				text.state.padding = 15;
			}

			const transform = {
				position: pos,
				rotation: TransformVector.zero(),
			};
			const sceneObject: SceneObject = {
				id,
				articleId,
				parentId: null,
				node,
				transform,
				addedWithClick,
			};
			this.sceneItems.set(id, sceneObject);
			return sceneObject;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('ObjectManager, createObject failed', error);
		}

		return null;
	}

	/**
	 * Creates a an empty object.
	 * Object will not be registered at sceneItems map.
	 *
	 * @param articleId Article id.
	 * @param position Object position.
	 * @param id Optional object id.
	 *
	 */
	public createEmptyObject(
		articleId: string,
		position: TransformPosition = TransformVector.zero(),
		id = this.getId()
	): SceneObject | null {
		const { x, y, z } = position;
		const model = this.connectionManager.getModelData(articleId);
		if (!model) {
			return null;
		}
		const size = model.bounds['default'].size;
		const pos = { x, y: y ? y : size.height / 2, z };

		try {
			const node = this.view3d.createObject(id, pos);

			const transform = {
				position: pos,
				rotation: TransformVector.zero(),
			};
			const sceneObject: SceneObject = {
				id,
				articleId,
				parentId: null,
				node,
				transform,
			};
			return sceneObject;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('ObjectManager, createEmptyObject failed', error);
		}

		return null;
	}

	/**
	 * Creates a an empty Node. With no components.
	 *
	 * @param position Object position.
	 * @param id Optional object id.
	 *
	 */
	public createEmptyNode(
		position: TransformPosition = TransformVector.zero(),
		id = this.getId()
	): Node {
		return this.view3d.createObject(id, position);
	}

	/**
	 * Destroys a Node.
	 *
	 * @param node Node.
	 *
	 */
	public destroyNode(node: Node): void {
		this.view3d.destroyObject(node);
	}

	/**
	 * Destroys an Object.
	 *
	 * @param item Object or object id.
	 *
	 */
	public destroyObject(item: SceneObject | string): void {
		const object = this.getObject(item);

		const children = this.connectionManager.getChildren(object.id);
		const sceneItems = this.sceneItems;
		children.forEach((id) => {
			const sceneItem = sceneItems.get(id);
			if (sceneItem) {
				this.destroyObject(sceneItem);
			} else {
				throw 'SceneContainer: destroySceneObject(): sceneItem does not exist';
			}
		});
		this.sceneItems.delete(object.id);
		this.moduleManager.disconnect(object);

		if (object.node) {
			this.view3d.destroyObject(object.node);
		}
	}

	/**
	 * Set Object Position.
	 * It will set the position to object.transform and it's node.
	 *
	 * @param item Object or object id.
	 * @param position New position.
	 *
	 */
	public setPosition(item: SceneObject | string, position: TransformPosition): void {
		const object = this.getObject(item);
		const transformPosition = object.transform.position as TransformPosition;
		Object.entries(position).forEach(([key, value]) => (transformPosition[key] = value));
		const { node } = object;
		if (node) {
			node.setLocalPosition(position);
		}
	}

	/**
	 * Set Object Rotation.
	 * It will set the rotation to object.transform and it's node.
	 *
	 * @param item Object or object id.
	 * @param rotation New rotation.
	 *
	 */
	public setRotation(item: SceneObject | string, rotation: TransformRotation): void {
		const object = this.getObject(item);
		const transformRotation = object.transform.rotation as TransformPosition;
		Object.entries(rotation).forEach(([key, value]) => (transformRotation[key] = value));
		const { node } = object;
		if (node) {
			node.setRotation(rotation);
		}
	}

	/**
	 * Check if URL is preloaded..
	 *
	 * @param url Url that can be used as MeshRendered.state.mesh.
	 *
	 */
	public isPreloaded(url: string): boolean {
		return this.view3d.isPreloaded(url);
	}

	/**
	 * Returns current color id.
	 *
	 */
	public getColorId(): string {
		if (!this.colorId) {
			this.updateColorId();
		}
		return this.colorId;
	}

	/**
	 * Set item to be invisible.
	 * Sets visibility of all meshRenderers in item's node to 0.
	 *
	 * @param item Object or object id.
	 */
	public hideItem(item: SceneObject | string): void {
		const { node } = this.getObject(item);
		if (!node) {
			return;
		}
		const children = node.getAllChildNodes();
		node.visibility = 0;
		children.forEach((n) => (n.visibility = 0));
	}

	/**
	 * Set item to be visible.
	 * Sets visibility of all meshRenderers in item's node to 1.
	 *
	 * @param item Object or object id.
	 */
	public unhideItem(item: SceneObject | string): void {
		const { node } = this.getObject(item);
		if (!node) {
			return;
		}
		const children = node.getAllChildNodes();
		node.visibility = 1;
		children.forEach((n) => (n.visibility = 1));
	}

	/**
	 * Disables item component.
	 *
	 * @param item Object or object id.
	 * @param componentName Component name.
	 */
	public disableComponent(item: SceneObject | string, componentName: string): void {
		const object = this.getObject(item);
		if (object) {
			const { node } = object;
			if (!node) {
				return;
			}
			const children = node.getAllChildNodes();
			const component = node.getComponent(componentName);
			if (component) {
				component.isDisabled = true;
			}
			children.forEach((n) => {
				const component = n.getComponent(componentName);
				if (component) {
					component.isDisabled = true;
				}
			});
		}
	}

	/**
	 * Enables item component.
	 *
	 * @param item Object or object id.
	 * @param componentName Component name.
	 */
	public enableComponent(item: SceneObject | string, componentName: string): void {
		const object = this.getObject(item);
		if (object) {
			const { node } = object;
			if (!node) {
				return;
			}
			const children = node.getAllChildNodes();
			const component = node.getComponent(componentName);
			if (component) {
				component.isDisabled = false;
			}
			children.forEach((n) => {
				const component = n.getComponent(componentName);
				if (component) {
					component.isDisabled = false;
				}
			});
		}
	}

	/**
	 * Sets cover for all scene items.
	 * Iterates over scene items and sets and updates their meshRenderers.
	 *
	 * @param colorId Color name.
	 */
	public setCoverForAllItems(colorId?: string): void {
		const color = colorId
			? colorId
			: this.props.rangeSettings.shouldColorAllModules
			? this.getColorId()
			: null;

		this.sceneItems.forEach((item) => {
			this.setCover(item, color);
		});
	}

	/**
	 * Sets cover for one item.
	 * Updates item meshRenderer with a new URL.
	 * Can hide grand children of an items if showing functions.
	 *
	 * @param item Object or object id.
	 * @param colorId Color name.
	 */
	public setCover(item: SceneObject | string, colorId: string | null): void {
		const object = this.getObject(item);
		const { node, articleId } = object;

		const model = this.connectionManager.getModelData(articleId);
		let renderer: MeshRenderer | null = null;

		if (node && model) {
			renderer = node.getComponent('MeshRenderer') as MeshRenderer;
			if (!renderer) {
				return;
			}
		} else {
			return;
		}

		// Check if model exists in selected cover else use old cover
		const productId = colorId
			? ProductUtils.getProductId(this.props.products, model.modelId, colorId)
			: null;
		// updating article id's for the selected cover if cover exists
		object.articleId = productId ? productId : articleId;

		const showFeatures = this.showFeatures(model);
		if (this.hasFeature(model) && this.props.rangeSettings.hideWhenShowingFunctions) {
			/* Show or hide childs and/or grand childs for the selected module */
			const childNodes = node.getAllChildNodes();

			childNodes.forEach((child) => {
				this.toggleVisibility(child, node.id, showFeatures);
				if (this.props.rangeSettings.hideGrandChildrenWhenShowingFunctions) {
					child.getAllChildNodes().forEach((n) => {
						this.toggleVisibility(n, child.id, showFeatures);
					});
				}
			});
		}

		const color = productId
			? (colorId as string)
			: (ProductUtils.getColorIdFromProduct(this.props.products, articleId) as string);

		const assetUrl = AssetsUrls.createUrlForAssetId(
			this.props.assetFolder,
			this.props.rangeSettings.assets3dSrc,
			model.modelId,
			this.props.rangeSettings.modelSwitchMap || null,
			color,
			showFeatures
		);

		// updating meshRenderer component
		const { state } = renderer;
		const isPreloaded = this.view3d.isPreloaded(assetUrl);
		if (!isPreloaded) {
			const size = model.bounds['default'].size;
			state.mesh = size;
			return;
		}
		state.mesh = assetUrl;
	}

	/**
	 * Enable or disable item overlay.
	 * This can and will be overwritten by uiController.selectionUi.
	 *
	 * @param node Object node.
	 * @param value Show/not show.
	 * @param inherit Apply to all child objects.
	 */
	public setOverlay(node: Node, value: boolean, inherit = true): void {
		const renderComponent = node.getComponent('MeshRenderer') as MeshRenderer;
		if (renderComponent) {
			renderComponent.state.overlayColor = { r: 1, g: 1, b: 1 };
			renderComponent.state.enableOverlay = value;
		}
		if (inherit && node.children.size) {
			node.children.forEach((n) => this.setOverlay(n, value));
		}
	}

	/**
	 * Gets and object.
	 *
	 * @param item Object or object id.
	 */
	public getObject(item: SceneObject | string): SceneObject {
		return (typeof item == 'string' ? this.sceneItems.get(item) : item) as SceneObject;
	}

	/**
	 * Toggles visibility of a scene node.
	 *
	 * @param node Node.
	 * @param parentId String - Node id of parent.
	 * @param hideNode Booean - Iff node should be hidden.
	 */
	private toggleVisibility(node: Node, parentId: string, hideNode: boolean): void {
		const sceneItems = this.sceneItems;
		const sceneItem = sceneItems.get(node.id);
		if (sceneItem) {
			if (sceneItem.parentId === parentId) {
				const family = this.connectionManager.getFamily(sceneItem.articleId);
				if (
					this.props.rangeSettings &&
					this.props.rangeSettings.hideWhenShowingFunctions &&
					this.props.rangeSettings.hideWhenShowingFunctions.includes(family)
				) {
					hideNode ? (node.visibility = 0) : (node.visibility = 1);
				}
			}
		} else {
			throw 'SceneContainer: toggleVisibility(): sceneItem does not exist';
		}
	}

	/**
	 * Has the model features.
	 *
	 * @param model Model.
	 * @returns If model has features.
	 */
	private hasFeature(model: Model): boolean {
		return model?.logic?.hasFeature || false;
	}

	/**
	 * Should feature be shown for this model?
	 *
	 * @param model Model.
	 * @returns If the model has a feature that should be shown.
	 */
	private showFeatures(model: Model): boolean {
		if (this.hasFeature(model)) {
			const showFunctionSleep =
				this.props.rangeSettings.modelsWithSleepFunction?.includes(model.modelId) &&
				this.props.showFunctionSleep;
			const showFunctionStorage =
				this.props.rangeSettings.modelsWithStorageFunctions?.includes(model.modelId) &&
				this.props.showFunctionStorage;

			return showFunctionSleep || showFunctionStorage || this.props.showFunctions || false;
		}
		return false;
	}

	/**
	 * Returns next ID.
	 *
	 */
	private getId(): string {
		const id = String(this.id);
		this.id++;
		return id;
	}

	/**
	 * Updates colorId.
	 *
	 */
	private updateColorId(): void {
		this.colorId = ProductUtils.getColorIdFromProduct(
			this.props.products,
			this.props.cover.articleId
		) as string;
	}
}
